import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, BadgeCheck, Building2, Copy, Download, Eye, HeartHandshake, Megaphone, Network, Send, Store, UsersRound } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';


const PROFILES = [
  {
    id: 'owners-360',
    title: 'Proprietari · Gestione 360°',
    audience: 'Proprietari di immobili',
    description: 'Il profilo ufficiale per chi affida a Holiday World la gestione completa dell’immobile: valorizzazione, distribuzione, ospitalità e controllo operativo.',
    meta: '16 pagine · Profilo ufficiale',
    image: '/company-profiles/proprietari-gestione-360.jpg',
    preview: '/downloads/company-profile-proprietari/company-profile-proprietari-completo-16-pagine.pdf',
    pdf: '/downloads/company-profile-proprietari/company-profile-proprietari-completo-16-pagine.pdf',
    icon: Building2,
    accent: 'border-blue-200 bg-blue-50 text-blue-900',
  },
  {
    id: 'welfare',
    title: 'Welfare aziendale',
    audience: 'Aziende e dipendenti',
    description: 'La proposta Holiday World dedicata al welfare: soggiorni, esperienze e benefit concreti per valorizzare le persone e rafforzare il legame con l’azienda.',
    meta: '12 pagine · Catalogo approvato',
    image: '/welfare/pages/p1.jpg',
    preview: '/welfare/',
    pdf: '/presentazione-holiday-world-welfare.pdf',
    icon: HeartHandshake,
    accent: 'border-emerald-200 bg-emerald-50 text-emerald-900',
  },
  {
    id: 'sponsor-tour',
    title: 'Sponsor Holiday World Tour',
    audience: 'Brand e partner commerciali',
    description: 'Il profilo definitivo per aziende interessate a sponsorizzare Holiday World Tour, con format, pubblico, visibilità e opportunità di partnership.',
    meta: '22 pagine · Profilo definitivo',
    image: '/companyprofile-eventi/pages/p1.jpg',
    preview: '/companyprofile-eventi/',
    pdf: '/api/public/companyprofile-eventi/pdf',
    icon: Megaphone,
    accent: 'border-amber-200 bg-amber-50 text-amber-950',
  },
  {
    id: 'vetrina',
    title: 'Vetrina Holiday World',
    audience: 'Proprietari autonomi',
    description: 'Il canale commerciale per chi gestisce già il proprio immobile e vuole aggiungere visibilità, domanda qualificata e Itinerario AI.',
    meta: '13 pagine · Profilo pronto',
    image: '/company-profiles/vetrina-holiday-world.jpg',
    preview: '/downloads/company-profile-vetrina/company-profile-vetrina-holiday-world-13-pagine.pdf',
    pdf: '/downloads/company-profile-vetrina/company-profile-vetrina-holiday-world-13-pagine.pdf',
    icon: Store,
    accent: 'border-cyan-200 bg-cyan-50 text-cyan-950',
  },
  {
    id: 'channel-manager',
    title: 'Channel Manager',
    audience: 'Proprietari e gestori',
    description: 'La presentazione del Channel Manager Holiday World: fino a 100 piattaforme, sincronizzazione, Vetrina collegata e Itinerario AI.',
    meta: '12 pagine · Bozza in revisione',
    image: '/channel-manager/pages/p1.jpg',
    preview: '/channel-manager/',
    pdf: '/downloads/company-profile-channel-manager/company-profile-channel-manager-holiday-world-12-pagine.pdf',
    icon: Network,
    accent: 'border-teal-200 bg-teal-50 text-teal-950',
  },
  {
    id: 'collaboratori',
    title: 'Collaboratori Holiday World',
    audience: 'Segnalatori, Agenti e Partner',
    description: 'Le tre formule per collaborare con Holiday World, con formazione, strumenti, compensi su immobili, rete e prenotazioni referral.',
    meta: '12 pagine · Profilo in revisione',
    image: '/collaboratori/pages/p1.jpg',
    preview: '/collaboratori/',
    pdf: '/downloads/company-profile-collaboratori/company-profile-collaboratori-holiday-world-12-pagine.pdf',
    icon: UsersRound,
    accent: 'border-green-200 bg-green-50 text-green-950',
  },
];


const absoluteUrl = (path) => `${window.location.origin}${path}`;


const safeCopy = async (value) => {
  if (navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(value);
      return true;
    } catch (error) {
      // Alcuni browser espongono Clipboard API ma ne negano il permesso.
    }
  }
  try {
    const textarea = document.createElement('textarea');
    textarea.value = value;
    textarea.setAttribute('readonly', '');
    textarea.style.position = 'fixed';
    textarea.style.opacity = '0';
    document.body.appendChild(textarea);
    textarea.select();
    const copied = document.execCommand('copy');
    document.body.removeChild(textarea);
    return copied;
  } catch (error) {
    return false;
  }
};


export default function CompanyProfilesPage() {
  const navigate = useNavigate();

  const copyLink = async (profile) => {
    const copied = await safeCopy(absoluteUrl(profile.preview));
    if (copied) toast.success(`Link ${profile.title} copiato`);
    else toast.error('Copia non consentita dal browser: apri il profilo e copia il link dalla barra indirizzi');
  };

  const shareProfile = async (profile) => {
    const url = absoluteUrl(profile.preview);
    if (navigator.share) {
      try {
        await navigator.share({ title: profile.title, text: profile.description, url });
        return;
      } catch (error) {
        if (error?.name === 'AbortError') return;
      }
    }
    const copied = await safeCopy(url);
    if (copied) toast.success('Link copiato: puoi inviarlo via email o WhatsApp');
    else toast.error('Condivisione non disponibile: apri il profilo e usa il menu Condividi del browser');
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="company-profiles-page">
      <header className="border-b border-slate-200 bg-white">
        <div className="mx-auto max-w-7xl px-4 py-7 sm:px-6 lg:px-8">
          <Button variant="ghost" size="sm" onClick={() => navigate(-1)} className="mb-5 -ml-2" data-testid="company-profiles-back-button">
            <ArrowLeft className="mr-2 h-4 w-4" /> Indietro
          </Button>
          <div className="max-w-3xl">
            <div className="mb-3 flex items-center gap-2 text-sm font-semibold uppercase text-blue-800">
              <BadgeCheck className="h-4 w-4" /> Raccolta amministrativa
            </div>
            <h1 className="text-4xl font-bold text-slate-950 sm:text-5xl" data-testid="company-profiles-title">Company Profile</h1>
            <p className="mt-4 text-base leading-relaxed text-slate-600 sm:text-lg">
              Raccolta riservata all’amministrazione. Apri, scarica o condividi profili ufficiali e versioni in revisione; i singoli link inviati restano accessibili ai destinatari.
            </p>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 gap-7 lg:grid-cols-3">
          {PROFILES.map(profile => {
            const Icon = profile.icon;
            return (
              <article key={profile.id} className="overflow-hidden rounded-md border border-slate-200 bg-white shadow-sm" data-testid={`company-profile-card-${profile.id}`}>
                <div className="aspect-[16/10] overflow-hidden bg-slate-100">
                  <img src={profile.image} alt={`Copertina ${profile.title}`} className="h-full w-full object-cover object-center" data-testid={`company-profile-cover-${profile.id}`} />
                </div>
                <div className="p-5 sm:p-6">
                  <div className={`inline-flex items-center gap-2 rounded border px-2.5 py-1 text-xs font-semibold ${profile.accent}`}>
                    <Icon className="h-4 w-4" /> {profile.audience}
                  </div>
                  <h2 className="mt-4 text-xl font-bold text-slate-950" data-testid={`company-profile-title-${profile.id}`}>{profile.title}</h2>
                  <p className="mt-3 min-h-[84px] text-sm leading-relaxed text-slate-600">{profile.description}</p>
                  <p className="mt-3 text-xs font-semibold uppercase text-slate-500" data-testid={`company-profile-meta-${profile.id}`}>{profile.meta}</p>

                  <div className="mt-6 grid grid-cols-2 gap-2">
                    <Button variant="outline" onClick={() => window.open(profile.preview, '_blank', 'noopener,noreferrer')} data-testid={`company-profile-preview-${profile.id}`}>
                      <Eye className="mr-2 h-4 w-4" /> Apri
                    </Button>
                    <Button onClick={() => window.open(profile.pdf, '_blank', 'noopener,noreferrer')} className="bg-blue-900 hover:bg-blue-800" data-testid={`company-profile-download-${profile.id}`}>
                      <Download className="mr-2 h-4 w-4" /> PDF
                    </Button>
                    <Button variant="outline" onClick={() => copyLink(profile)} data-testid={`company-profile-copy-${profile.id}`}>
                      <Copy className="mr-2 h-4 w-4" /> Copia link
                    </Button>
                    <Button variant="outline" onClick={() => shareProfile(profile)} data-testid={`company-profile-share-${profile.id}`}>
                      <Send className="mr-2 h-4 w-4" /> Invia
                    </Button>
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      </main>
    </div>
  );
}